import React from 'react';
import { Button } from '@material-ui/core';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import {
  AppDatePicker,
  AppSelect,
  AppTextField,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { Translations } from '../../configuration';
import NavigationConfig from 'navigation/config';
import { useHistory } from 'react-router';
import * as DateUtils from 'utils/dates';
import useQuery from 'custom_hooks/useQuery';

interface FormFields {
  vatNumber: string | null;
  notificationType: string | null;
  notificationDateFrom: Date | null;
  notificationDateTo: Date | null;
}

interface IProps {
  vatNumber: string | null;
  notificationType: string | null;
  notificationDateFrom: Date | null;
  notificationDateTo: Date | null;
}

const notificationTypeOptions = [
  'AdminMessage',
  'Email',
  'Registration Submit',
  'Registration Approve',
  'VAT Return Submit',
  'Payment Reminder MSID',
  'Payment Reminder MSCON',
  'VAT Return Submit Reminder',
];

const FilterForm = (props: IProps) => {
  const history = useHistory();
  const queryParams = useQuery();
  const defaultValues = {
    vatNumber: props.vatNumber ? props.vatNumber : null,
    notificationType: props.notificationType ? props.notificationType : null,
    notificationDateFrom: props.notificationDateFrom
      ? props.notificationDateFrom
      : null,
    notificationDateTo: props.notificationDateTo
      ? props.notificationDateTo
      : null,
  };

  const { control, errors, handleSubmit, reset } = useForm<FormFields>({
    defaultValues,
  });

  React.useEffect(() => {
    reset(defaultValues);
  }, [
    props.vatNumber,
    props.notificationType,
    props.notificationDateFrom,
    props.notificationDateTo,
    reset,
  ]);

  const onSubmit = (data: FormFields) => {
    const nextParams = new URLSearchParams(queryParams.toString());
    nextParams.delete('notificationVatNumber');
    nextParams.delete('notificationType');
    nextParams.delete('notificationDateFrom');
    nextParams.delete('notificationDateTo');
    nextParams.delete('notificationSortField');
    nextParams.delete('notificationPage');
    nextParams.delete('notificationPageSize');
    nextParams.delete('notificationOrder');

    if (data.vatNumber) nextParams.set('notificationVatNumber', data.vatNumber);
    if (data.notificationType)
      nextParams.set('notificationType', data.notificationType);
    if (data.notificationDateFrom)
      nextParams.set(
        'notificationDateFrom',
        DateUtils.DateInQueryFormat(data.notificationDateFrom)
      );
    if (data.notificationDateTo)
      nextParams.set(
        'notificationDateTo',
        DateUtils.DateInQueryFormat(data.notificationDateTo)
      );

    const queryString = nextParams.toString();
    history.push(
      NavigationConfig.notificationsPage().path +
        (queryString ? `?${queryString}` : '')
    );
  };

  const clearFilter = () => {
    reset({
      vatNumber: null,
      notificationType: null,
      notificationDateFrom: null,
      notificationDateTo: null,
    });
    const nextParams = new URLSearchParams(queryParams.toString());
    nextParams.delete('notificationVatNumber');
    nextParams.delete('notificationType');
    nextParams.delete('notificationDateFrom');
    nextParams.delete('notificationDateTo');
    nextParams.delete('notificationSortField');
    nextParams.delete('notificationPage');
    nextParams.delete('notificationPageSize');
    nextParams.delete('notificationOrder');
    const queryString = nextParams.toString();
    history.push(
      NavigationConfig.notificationsPage().path +
        (queryString ? `?${queryString}` : '')
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ marginBottom: '0.6rem' }}>
      <GridLayout
        elements={
          [
            {
              element: renderVatNumberField(errors, control),
              size: 3,
              id: 'notificationVatNumber',
            },
            {
              element: renderNotificationTypeField(errors, control),
              size: 3,
              id: 'notificationType',
            },
            {
              element: renderNotificationDateFromField(errors, control),
              size: 2,
              id: 'notificationDateFrom',
            },
            {
              element: renderNotificationDateToField(errors, control),
              size: 2,
              id: 'notificationDateTo',
            },
            {
              element: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{
                      width: '47%',
                      marginTop: '0.6rem',
                      marginRight: '0.25rem',
                    }}
                  >
                    <TranslatedText textMap={Translations.search} />
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{
                      width: '47%',
                      marginTop: '0.6rem',
                      marginLeft: '0.25rem',
                    }}
                    onClick={clearFilter}
                  >
                    <TranslatedText textMap={Translations.clear} />
                  </Button>
                </div>
              ),
              size: 2,
              id: 'notificationActions',
            },
          ] as any
        }
      />
    </form>
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      id="vatNumber"
      label={
        <TranslatedText textMap={Translations.vat_number_search_control} />
      }
      error={'vatNumber' in errors}
      name="vatNumber"
      control={control}
      rules={{}}
      helperText={errors.vatNumber && errors.vatNumber.message}
    />
  );
};

const renderNotificationTypeField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  let options: any[] = [
    {
      id: null,
      label: Translations.notification_type_search_control.English,
    },
  ];

  notificationTypeOptions.forEach((type) =>
    options.push({
      id: type,
      label: type,
    })
  );

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={
        <TranslatedText
          textMap={Translations.notification_type_search_control}
        />
      }
      id="notificationType"
      name="notificationType"
      error={'notificationType' in errors}
      control={control}
      rules={{}}
      options={options}
      helperText={errors.notificationType && errors.notificationType.message}
    />
  );
};

const renderNotificationDateFromField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      id="notificationDateFrom"
      label={<TranslatedText textMap={Translations.notification_date_from} />}
      error={'notificationDateFrom' in errors}
      name="notificationDateFrom"
      control={control}
      rules={{}}
      autoOk
    />
  );
};

const renderNotificationDateToField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      id="notificationDateTo"
      label={<TranslatedText textMap={Translations.notification_date_to} />}
      error={'notificationDateTo' in errors}
      name="notificationDateTo"
      control={control}
      rules={{}}
      autoOk
    />
  );
};

export default FilterForm;
